import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/i18n/I18nProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Upload,
  ScanLine,
  FileSpreadsheet,
  Loader2,
  Trash2,
  Save,
  UserPlus,
  UserCheck,
  CheckCircle2,
  Paperclip,
  X,
  AlertTriangle,
} from "lucide-react";

const TABS = [
  { key: "single", label: "Attestati (PDF/JPG)", icon: ScanLine },
  { key: "excel", label: "Excel massivo", icon: FileSpreadsheet },
];

// Concorrenza massima per l'analisi AI batch (evita picchi di richieste)
const MAX_PARALLEL = 2;

export default function ImportAttestatiPage() {
  const { lang } = useI18n();
  const [params] = useSearchParams();
  const initialCompany = params.get("company_id") || "";
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState(initialCompany);
  const [subtypes, setSubtypes] = useState([]);
  const [tab, setTab] = useState("single");

  // Multi-certificate state — array di { _uid, file, status, parsed?, error? }
  // status: "pending" | "analyzing" | "done" | "error"
  const [certItems, setCertItems] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const singleRef = useRef(null);

  // Excel state
  const [xlsFile, setXlsFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [xlsMapping, setXlsMapping] = useState(null);
  const excelRef = useRef(null);

  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);

  const loadCompanies = useCallback(async () => {
    // Chiamata senza `page`: backend ritorna array piatto con tutte le aziende
    // (tenant-scoped, max 5000) — necessario per popolare il dropdown completo.
    const r = await api.get("/companies");
    const items = Array.isArray(r.data) ? r.data : (r.data.items || []);
    setCompanies(items);
  }, []);

  useEffect(() => {
    loadCompanies();
    api.get("/deadlines/catalog").then((r) => {
      const list = (r.data?.subtypes || []).filter((s) => s.category === "formazione");
      setSubtypes(list);
    });
  }, [loadCompanies]);

  // ---------------------- Multi-cert helpers ----------------------
  const addFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    const uidGen = () =>
      (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : `k-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    setCertItems((prev) => [
      ...prev,
      ...files.map((f) => ({ _uid: uidGen(), file: f, status: "pending", parsed: null, error: null, selected: true })),
    ]);
  };

  const removeItem = (uid) => setCertItems((prev) => prev.filter((it) => it._uid !== uid));

  const updateItemParsed = (uid, patch) =>
    setCertItems((prev) =>
      prev.map((it) =>
        it._uid === uid ? { ...it, parsed: { ...it.parsed, ...patch } } : it
      )
    );

  const updateItemLav = (uid, patch) =>
    setCertItems((prev) =>
      prev.map((it) =>
        it._uid === uid
          ? { ...it, parsed: { ...it.parsed, lavoratore: { ...it.parsed.lavoratore, ...patch } } }
          : it
      )
    );

  const updateItemCorso = (uid, patch) =>
    setCertItems((prev) =>
      prev.map((it) =>
        it._uid === uid
          ? { ...it, parsed: { ...it.parsed, corso: { ...it.parsed.corso, ...patch } } }
          : it
      )
    );

  const toggleSelect = (uid, v) =>
    setCertItems((prev) => prev.map((it) => (it._uid === uid ? { ...it, selected: v } : it)));

  // Analizza in parallelo con concorrenza limitata
  const analyzeAll = async () => {
    if (!companyId) return toast.error("Seleziona un'azienda");
    const pending = certItems.filter((it) => it.status === "pending" || it.status === "error");
    if (pending.length === 0) return toast.error("Nessun file da analizzare");
    setAnalyzing(true);

    const analyzeOne = async (item) => {
      setCertItems((prev) => prev.map((it) => (it._uid === item._uid ? { ...it, status: "analyzing", error: null } : it)));
      const fd = new FormData();
      fd.append("file", item.file);
      fd.append("company_id", companyId);
      try {
        const r = await api.post("/attestati/parse-certificate", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 180000,
        });
        setCertItems((prev) =>
          prev.map((it) =>
            it._uid === item._uid
              ? { ...it, status: "done", parsed: r.data, error: null }
              : it
          )
        );
      } catch (err) {
        setCertItems((prev) =>
          prev.map((it) =>
            it._uid === item._uid
              ? { ...it, status: "error", error: err.response?.data?.detail || "AI error" }
              : it
          )
        );
      }
    };

    // Semplice worker pool
    const queue = [...pending];
    const workers = Array.from({ length: Math.min(MAX_PARALLEL, queue.length) }, async () => {
      while (queue.length > 0) {
        const next = queue.shift();
        if (next) await analyzeOne(next);
      }
    });
    await Promise.all(workers);
    setAnalyzing(false);
    toast.success(`${pending.length} attestati elaborati`);
  };

  const commitMultiCert = async () => {
    const readyItems = certItems.filter((it) => it.status === "done" && it.selected !== false);
    if (readyItems.length === 0) return toast.error("Nessun attestato pronto da salvare");
    setCommitting(true);
    try {
      const r = await api.post("/attestati/commit", {
        company_id: companyId,
        rows: readyItems.map((it) => ({
          lavoratore: { ...it.parsed.lavoratore, worker_id: it.parsed.lavoratore.existing_worker?.id },
          corso: it.parsed.corso,
          attachment_url: it.parsed.attachment_url || null,
          attachment_filename: it.parsed.attachment_filename || null,
        })),
      });
      setResult(r.data);
      setCertItems([]);
      if (singleRef.current) singleRef.current.value = "";
      toast.success(
        `Salvati ${readyItems.length} attestati — lavoratori: +${r.data.created_workers} (aggiornati ${r.data.updated_workers}) · scadenze: +${r.data.created_deadlines}`
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore salvataggio");
    }
    setCommitting(false);
  };

  // ---------------------- Excel helpers ----------------------
  const parseExcel = async () => {
    if (!companyId) return toast.error("Seleziona un'azienda");
    if (!xlsFile) return toast.error("Carica un file Excel (.xlsx)");
    const fd = new FormData();
    fd.append("file", xlsFile);
    fd.append("company_id", companyId);
    try {
      const r = await api.post("/attestati/parse-excel", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      setRows(r.data.rows || []);
      setXlsMapping(r.data.mapping);
      toast.success(`${r.data.totale_righe} righe mappate — verifica e salva`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI error");
    }
  };

  const commitExcel = async () => {
    const selected = rows.filter((r) => r._selected !== false);
    if (selected.length === 0) return toast.error("Nessuna riga selezionata");
    setCommitting(true);
    try {
      const r = await api.post("/attestati/commit", {
        company_id: companyId,
        rows: selected.map((row) => ({
          lavoratore: { ...row.lavoratore, worker_id: row.lavoratore.existing_worker?.id },
          corso: row.corso,
        })),
      });
      setResult(r.data);
      setRows([]);
      if (excelRef.current) excelRef.current.value = "";
      toast.success(
        `Salvate ${selected.length} righe — lavoratori: +${r.data.created_workers} (aggiornati ${r.data.updated_workers}) · scadenze: +${r.data.created_deadlines}`
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore salvataggio");
    }
    setCommitting(false);
  };

  const updateRow = (i, patch) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const updateRowLav = (i, patch) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, lavoratore: { ...r.lavoratore, ...patch } } : r)));
  const updateRowCorso = (i, patch) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, corso: { ...r.corso, ...patch } } : r)));

  const companyName = companies.find((c) => c.id === companyId)?.name;
  const doneCount = certItems.filter((it) => it.status === "done").length;
  const selectedCount = certItems.filter((it) => it.status === "done" && it.selected !== false).length;
  const pendingCount = certItems.filter((it) => it.status === "pending" || it.status === "error").length;

  return (
    <div className="space-y-6" data-testid="import-attestati">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Import attestati e corsi</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Carica uno o <b>più attestati</b> (PDF/JPG) o un file Excel — l'AI estrae automaticamente <b>nome, cognome,
          C.F., tipo corso, data e scadenza</b>. Il sistema crea/aggiorna anagrafica lavoratore, scadenza corso
          associata e <b>salva l'attestato come allegato della scadenza</b>.
        </p>
      </div>

      {/* Selezione azienda */}
      <div className="border border-border rounded-md p-4 bg-muted/20 space-y-2">
        <Label>Azienda destinataria</Label>
        <Select value={companyId} onValueChange={setCompanyId}>
          <SelectTrigger className="w-full max-w-md" data-testid="ia-company">
            <SelectValue placeholder="Seleziona un'azienda…" />
          </SelectTrigger>
          <SelectContent>
            {companies.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {companyName && (
          <p className="text-xs text-muted-foreground">
            I dati verranno creati per: <b>{companyName}</b>
          </p>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-border flex gap-1">
        {TABS.map((tb) => (
          <button
            key={tb.key}
            onClick={() => setTab(tb.key)}
            className={`px-4 py-2 text-sm flex items-center gap-2 border-b-2 -mb-px transition ${
              tab === tb.key ? "border-primary text-primary font-semibold" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
            data-testid={`ia-tab-${tb.key}`}
          >
            <tb.icon className="w-4 h-4" /> {tb.label}
          </button>
        ))}
      </div>

      {/* Tab 1: Attestati multi-upload */}
      {tab === "single" && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <input
              ref={singleRef}
              type="file"
              accept=".pdf,image/*"
              hidden
              multiple
              onChange={(e) => {
                addFiles(e.target.files);
                e.target.value = "";
              }}
            />
            <Button
              variant="outline"
              onClick={() => singleRef.current?.click()}
              className="rounded-full"
              data-testid="ia-cert-upload"
            >
              <Upload className="w-4 h-4 mr-1.5" /> Aggiungi attestati (multipli)
            </Button>
            <Button
              onClick={analyzeAll}
              disabled={!companyId || analyzing || pendingCount === 0}
              className="rounded-full"
              data-testid="ia-cert-parse"
            >
              {analyzing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <ScanLine className="w-4 h-4 mr-1.5" />}
              Analizza {pendingCount > 0 ? `${pendingCount} ` : ""}con AI
            </Button>
            {certItems.length > 0 && (
              <div className="text-xs text-muted-foreground ml-auto">
                {certItems.length} file · {doneCount} analizzati · {selectedCount} selezionati
              </div>
            )}
          </div>

          {certItems.length > 0 && (
            <>
              <div className="border border-border rounded-md overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-muted/50 text-[10px] uppercase tracking-widest text-muted-foreground">
                    <tr>
                      <th className="px-2 py-2 w-8"></th>
                      <th className="px-2 py-2 text-left">File / Stato</th>
                      <th className="px-2 py-2 text-left">Cognome / Nome</th>
                      <th className="px-2 py-2 text-left">Codice Fiscale</th>
                      <th className="px-2 py-2 text-left">Corso</th>
                      <th className="px-2 py-2 text-left">Data corso</th>
                      <th className="px-2 py-2 text-left">Scadenza</th>
                      <th className="px-2 py-2 text-left">Match</th>
                      <th className="px-2 py-2 w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {certItems.map((it) => (
                      <CertRow
                        key={it._uid}
                        item={it}
                        subtypes={subtypes}
                        onLav={(patch) => updateItemLav(it._uid, patch)}
                        onCorso={(patch) => updateItemCorso(it._uid, patch)}
                        onSelect={(v) => toggleSelect(it._uid, v)}
                        onRemove={() => removeItem(it._uid)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end">
                <Button
                  onClick={commitMultiCert}
                  disabled={committing || selectedCount === 0}
                  className="rounded-full"
                  data-testid="ia-cert-commit"
                >
                  {committing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
                  Salva {selectedCount} attestat{selectedCount === 1 ? "o" : "i"} con allegato
                </Button>
              </div>
            </>
          )}

          {certItems.length === 0 && (
            <div className="text-sm text-muted-foreground italic border border-dashed border-border rounded p-8 text-center">
              Trascina o carica uno o più attestati (PDF / JPG / PNG). Verranno analizzati con AI in parallelo e ognuno
              sarà salvato come allegato della relativa scadenza.
            </div>
          )}
        </div>
      )}

      {/* Tab 2: Excel */}
      {tab === "excel" && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <input
              ref={excelRef}
              type="file"
              accept=".xlsx,.xls"
              hidden
              onChange={(e) => setXlsFile(e.target.files?.[0] || null)}
            />
            <Button
              variant="outline"
              onClick={() => excelRef.current?.click()}
              className="rounded-full"
              data-testid="ia-xls-upload"
            >
              <Upload className="w-4 h-4 mr-1.5" /> {xlsFile ? xlsFile.name.slice(0, 40) : "Carica Excel (.xlsx)"}
            </Button>
            <Button onClick={parseExcel} disabled={!xlsFile || !companyId} className="rounded-full" data-testid="ia-xls-parse">
              <FileSpreadsheet className="w-4 h-4 mr-1.5" />
              Analizza foglio con AI
            </Button>
          </div>

          {rows.length > 0 && (
            <>
              <div className="text-xs text-muted-foreground">
                {rows.length} righe estratte. Deseleziona quelle da escludere prima di salvare.
                {xlsMapping && (
                  <span className="ml-2 italic">
                    Colonne mappate: {Object.entries(xlsMapping).filter(([, v]) => v !== null).map(([k]) => k).join(", ")}
                  </span>
                )}
              </div>
              <div className="border border-border rounded-md overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-muted/50 text-[10px] uppercase tracking-widest text-muted-foreground">
                    <tr>
                      <th className="px-2 py-2 w-8"></th>
                      <th className="px-2 py-2 text-left">Cognome / Nome</th>
                      <th className="px-2 py-2 text-left">Codice Fiscale</th>
                      <th className="px-2 py-2 text-left">Corso</th>
                      <th className="px-2 py-2 text-left">Data corso</th>
                      <th className="px-2 py-2 text-left">Scadenza</th>
                      <th className="px-2 py-2 text-left">Match anagrafica</th>
                      <th className="px-2 py-2 w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={`row-${i}`} className={`border-t border-border ${r._selected === false ? "opacity-40" : ""}`}>
                        <td className="px-2 py-2 text-center">
                          <input
                            type="checkbox"
                            checked={r._selected !== false}
                            onChange={(e) => updateRow(i, { _selected: e.target.checked })}
                            data-testid={`ia-row-sel-${i}`}
                          />
                        </td>
                        <td className="px-2 py-2">
                          <Input
                            value={r.lavoratore.last_name || ""}
                            onChange={(e) => updateRowLav(i, { last_name: e.target.value })}
                            className="h-7 text-xs"
                            placeholder="Cognome"
                          />
                          <Input
                            value={r.lavoratore.first_name || ""}
                            onChange={(e) => updateRowLav(i, { first_name: e.target.value })}
                            className="h-7 text-xs mt-1"
                            placeholder="Nome"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <Input
                            value={r.lavoratore.tax_code || ""}
                            onChange={(e) => updateRowLav(i, { tax_code: e.target.value.toUpperCase() })}
                            className={`h-7 text-xs font-mono ${r.lavoratore.tax_code && !r.lavoratore.tax_code_valid ? "border-rose-400" : ""}`}
                            placeholder="RSSMRA…"
                          />
                        </td>
                        <td className="px-2 py-2 min-w-[220px]">
                          <select
                            value={r.corso.subtype_key}
                            onChange={(e) => updateRowCorso(i, { subtype_key: e.target.value })}
                            className="w-full h-7 rounded border border-input bg-background px-2 text-xs"
                          >
                            {subtypes.map((s) => (
                              <option key={s.key} value={s.key}>
                                {s.label}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-2 py-2">
                          <Input
                            type="date"
                            value={r.corso.base_date || ""}
                            onChange={(e) => updateRowCorso(i, { base_date: e.target.value })}
                            className="h-7 text-xs"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <Input
                            type="date"
                            value={r.corso.due_date || ""}
                            onChange={(e) => updateRowCorso(i, { due_date: e.target.value })}
                            className="h-7 text-xs"
                          />
                        </td>
                        <td className="px-2 py-2 text-[11px]">
                          {r.lavoratore.existing_worker ? (
                            <span className="inline-flex items-center gap-1 text-emerald-700">
                              <UserCheck className="w-3 h-3" /> Esistente ({r.lavoratore.existing_worker.match})
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-muted-foreground">
                              <UserPlus className="w-3 h-3" /> Nuovo
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-2">
                          <button
                            onClick={() => setRows((rs) => rs.filter((_, idx) => idx !== i))}
                            className="text-muted-foreground hover:text-destructive"
                            data-testid={`ia-row-remove-${i}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end">
                <Button onClick={commitExcel} disabled={committing} className="rounded-full" data-testid="ia-xls-commit">
                  {committing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
                  Salva {rows.filter((r) => r._selected !== false).length} righe
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      {result && (
        <div className="border border-emerald-200 bg-emerald-50 rounded-md p-4">
          <div className="flex items-center gap-2 text-emerald-800 font-semibold text-sm">
            <CheckCircle2 className="w-4 h-4" /> Import completato
          </div>
          <div className="text-sm text-emerald-900 mt-1">
            Lavoratori creati: <b>{result.created_workers}</b> · aggiornati: <b>{result.updated_workers}</b> · scadenze
            create: <b>{result.created_deadlines}</b>
            {result.errors?.length > 0 && (
              <span className="text-amber-700"> · {result.errors.length} errori</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ------------------- Riga della tabella multi-attestato -------------------
function CertRow({ item, subtypes, onLav, onCorso, onSelect, onRemove }) {
  const parsed = item.parsed;
  const l = parsed?.lavoratore || {};
  const c = parsed?.corso || {};
  const isDone = item.status === "done";
  const disabled = !isDone;

  const statusPill = (() => {
    if (item.status === "pending")
      return <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200">Attesa</span>;
    if (item.status === "analyzing")
      return (
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-sky-100 text-sky-700 border border-sky-200 inline-flex items-center gap-1">
          <Loader2 className="w-3 h-3 animate-spin" /> Analisi…
        </span>
      );
    if (item.status === "error")
      return (
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 border border-rose-200 inline-flex items-center gap-1" title={item.error || ""}>
          <AlertTriangle className="w-3 h-3" /> Errore
        </span>
      );
    return <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 border border-emerald-200">Pronto</span>;
  })();

  return (
    <tr className={`border-t border-border ${!item.selected && isDone ? "opacity-40" : ""}`} data-testid={`ia-cert-row-${item._uid}`}>
      <td className="px-2 py-2 text-center">
        <input
          type="checkbox"
          disabled={!isDone}
          checked={item.selected !== false}
          onChange={(e) => onSelect(e.target.checked)}
          data-testid={`ia-cert-sel-${item._uid}`}
        />
      </td>
      <td className="px-2 py-2 min-w-[180px]">
        <div className="flex items-center gap-1.5">
          <Paperclip className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
          <span className="truncate text-[11px]" title={item.file.name}>{item.file.name}</span>
        </div>
        <div className="mt-1">{statusPill}</div>
        {item.status === "error" && item.error && (
          <div className="text-[10px] text-rose-700 mt-0.5 line-clamp-2">{item.error}</div>
        )}
      </td>
      <td className="px-2 py-2">
        <Input
          value={l.last_name || ""}
          onChange={(e) => onLav({ last_name: e.target.value })}
          disabled={disabled}
          className="h-7 text-xs"
          placeholder="Cognome"
        />
        <Input
          value={l.first_name || ""}
          onChange={(e) => onLav({ first_name: e.target.value })}
          disabled={disabled}
          className="h-7 text-xs mt-1"
          placeholder="Nome"
        />
      </td>
      <td className="px-2 py-2">
        <Input
          value={l.tax_code || ""}
          onChange={(e) =>
            onLav({
              tax_code: e.target.value.toUpperCase(),
              tax_code_valid: /^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]$/.test(e.target.value.toUpperCase()),
            })
          }
          disabled={disabled}
          className={`h-7 text-xs font-mono ${l.tax_code && !l.tax_code_valid ? "border-rose-400" : ""}`}
          placeholder="RSSMRA…"
        />
      </td>
      <td className="px-2 py-2 min-w-[220px]">
        <select
          value={c.subtype_key || ""}
          onChange={(e) => onCorso({ subtype_key: e.target.value })}
          disabled={disabled}
          className="w-full h-7 rounded border border-input bg-background px-2 text-xs"
        >
          <option value="">—</option>
          {subtypes.map((s) => (
            <option key={s.key} value={s.key}>
              {s.label}
            </option>
          ))}
        </select>
      </td>
      <td className="px-2 py-2">
        <Input
          type="date"
          value={c.base_date || ""}
          onChange={(e) => onCorso({ base_date: e.target.value })}
          disabled={disabled}
          className="h-7 text-xs"
        />
      </td>
      <td className="px-2 py-2">
        <Input
          type="date"
          value={c.due_date || ""}
          onChange={(e) => onCorso({ due_date: e.target.value })}
          disabled={disabled}
          className="h-7 text-xs"
        />
      </td>
      <td className="px-2 py-2 text-[11px]">
        {isDone && l.existing_worker ? (
          <span className="inline-flex items-center gap-1 text-emerald-700">
            <UserCheck className="w-3 h-3" /> {l.existing_worker.match}
          </span>
        ) : isDone ? (
          <span className="inline-flex items-center gap-1 text-muted-foreground">
            <UserPlus className="w-3 h-3" /> Nuovo
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
      <td className="px-2 py-2">
        <button
          onClick={onRemove}
          className="text-muted-foreground hover:text-destructive"
          data-testid={`ia-cert-remove-${item._uid}`}
          title="Rimuovi dalla lista"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </td>
    </tr>
  );
}
